import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import QRCode from "react-qr-code";
import cx from "classnames";
import Header from "../Header/header";
import styles from "./summary.module.scss";
import { useEffect } from "react";
import DownloadIcon from '@mui/icons-material/Download';


/* ----------------------------- Types (minimal) ----------------------------- */
type Patient = {
  title?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  mobile_phone?: string;
  home_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  nhs_number?: string;
};

type TreatmentPlan = {
  treatments?: { description: string }[];
};

type PlaylistItem = {
  id: string | number;
  title?: string;
  thumbnail?: string;
  status?: boolean;
};

type Playlist = {
  title?: string;
  status?: boolean;
  playlist_items?: PlaylistItem[];
};

type PlaylistActivityRow = {
  video_id: string | number;
  watch_percentage: number; // 0..1
  watch_status: "Not Watched" | "In Progress" | "Completed" | string;
  last_watched?: string | null;
  has_playback_issue?: boolean;
};

type SignatureMeta = {
  signed_type?: "manual" | "digital";
  signed_timestamp?: string | null;
};

type SummaryDTO = {
  id: string | number;
  version_no: number;
  created_at: string;
  status: "Draft" | "Sent" | "Completed" | string;
  is_old_version?: boolean;
  link?: string;
  patient?: Patient;
  treatment_plan?: TreatmentPlan;
  clinician_table_id?: string | number;
  playlists?: Playlist[];
  playlist_activity?: PlaylistActivityRow[];
  patient_signature?: SignatureMeta;
};

type VersionsRow = {
  consent_bundle_id: string;
  code: string;
  version: number;
};

/* ---------------------------- Utility functions ---------------------------- */
const formatDate = (iso?: string | null) => (iso ? new Date(iso).toDateString() : "-");
const percent = (x: number) => `${(Math.min(Math.max(x, 0), 1) * 100).toFixed(0)}%`;

/* ------------------------------- Component -------------------------------- */
export default function Summary() {
  const { consentBundleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"CONSENT" | "WAITING" | "AUDIT" | "DOCS">("CONSENT");
  const [showQR, setShowQR] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [versionList, setVersionList] = useState<VersionsRow[]>([]);
  const user: any = queryClient.getQueryData(["user"]);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewId, setQuickViewId] = useState<string | number | null>(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");
  const [interactionLog, setInteractionLog] = useState<any>({});

  const [copiedToast, setCopiedToast] = useState(false);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);




  /* ------------------------------- Data load ------------------------------ */
  const summaryQuery = useQuery(["summaryDetails", consentBundleId], async () => {
    const { data } = await axios.get<SummaryDTO>(`/consent_bundle/${consentBundleId}/activity_summary`);
    // Keep your existing behavior
    if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
      navigate(`/${(data as any).data.treatment_plan_id}`);
    }
    return data;
  });

  const data = summaryQuery.data;
  const patient = data?.patient;

  /* ------------------------------ Mutations ------------------------------ */
  const manuallySign = useMutation(
  async (id: string | number) =>
    axios.put(`/consent_bundle/${id}/mark_consent_bundle_as_signed`),
  {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
    },
  }
);

  const createNewVersion = useMutation(
    async (id: string | number) => axios.post(`/consent_bundle/${id}/create_consent_bundle_new_version`),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
        // Your original code navigates to dashboard after create
        navigate(`/new-dashboard`);
      },
    }
  );

  /* --------------------------- Derived bits --------------------------- */
  const treatmentTitle = useMemo(() => {
    const list = data?.treatment_plan?.treatments || [];
    if (!list.length) return "-";
    return list.map((t) => t.description).join(", ");
  }, [data]);

  const createdOn = formatDate(data?.created_at);
  const hasMultipleVersions = (data?.version_no ?? 1) > 1;

  // activity map (kept in case you show video status elsewhere)
  const activityMap = useMemo(() => {
    const map = new Map<string | number, PlaylistActivityRow>();
    (data?.playlist_activity || []).forEach((row) => map.set(row.video_id, row));
    return map;
  }, [data]);

  const visiblePlaylists = (data?.playlists || []).filter((p) => p.status);
  const anyVideoIssue = useMemo(() => (data?.playlist_activity || []).some((r) => r.has_playback_issue), [data]);

  /* --------------------------- Action handlers --------------------------- */
  const downloadLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const previewLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      window.open(href, "_blank", "noopener,noreferrer");
      // don’t revoke immediately; allow browser to load it
      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFullReport = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/download_full_consent_bundle_report`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadVersion = async (bundleId: string) => {
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${bundleId}/generate_pdf`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data || ({} as any)));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVersionClick = async () => {
    if (!hasMultipleVersions) return;

    if (showDropdown) {
      setShowDropdown(false);
      return;
    }

    try {
      const resp = await axios.get<VersionsRow[]>(`/consent_bundle/${consentBundleId}/get_consent_bundle_versions`);
      setVersionList(resp.data || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching versions:", err);
      setVersionList([]);
      setShowDropdown(true);
    }
  };

  const handleSelectVersion = async (bundleId: string | number) => {
    setShowDropdown(false);
    navigate(`/summary/${bundleId}`);
  };

  const copyRemoteLink = async () => {
  if (!data) return;

  const link =
    data.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`;

  await navigator.clipboard.writeText(link);

  setCopiedToast(true);

  setTimeout(() => {
    setCopiedToast(false);
  }, 1800);
};



    const loadAuditLog = async (id: string | number) => {
        setQuickViewLoading(true);
        setQuickViewError("");
        setInteractionLog({});

        try {
            const res = await axios.get(
            `/consent_bundle/${id}/interaction_log`
            );

            // API returns an object keyed by date
            setInteractionLog(res.data || {});
        } catch (err) {
            console.error(err);
            setQuickViewError("Failed to load audit log");
        } finally {
            setQuickViewLoading(false);
        }
        };


useEffect(() => {
  if (activeTab === "AUDIT" && consentBundleId) {
    loadAuditLog(consentBundleId);
  }
}, [activeTab, consentBundleId]);


const versionStatusLabel = (v: VersionsRow, latestVersion: number) => {
  if (v.version === latestVersion) return "Current";
  if (v.version < latestVersion) return "Archived";
  return "Signed";
};

useEffect(() => {
  if (activeTab === "DOCS" && consentBundleId) {
    handleVersionClick();
  }
}, [activeTab]);


const printOfflineConsent = async () => {
  if (!data) return;

  setIsDownloading(true);
  try {
    const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/generate_offline_pdf`;

    const resp = await axios.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(resp.data);

    // Open print dialog
    const win = window.open(href, "_blank");
    win?.addEventListener("load", () => {
      win.print();
    });

    setTimeout(() => URL.revokeObjectURL(href), 30_000);
  } finally {
    setIsDownloading(false);
  }
};


  /* -------------------------------- Render -------------------------------- */
  return (
    <>
      <Header />

      <div className={styles.page}>
        <div className={styles.headerBlock}>
          <h1 className={styles.pageTitle}>Summary</h1>
          <p className={styles.pageSubtitle}>Here’s a summary of your patient’s consent journey</p>
        </div>

        {/* Top orange bordered summary bar (like screenshot) */}
        <div className={styles.topBar}>
          <div className={styles.topBarRow}>
            <span
              className={cx(styles.statusPillTop, {
                [styles.statusCompleted]: data?.status === "Completed",
                [styles.statusOther]: data?.status !== "Completed",
              })}
            >
              {data?.status || "—"}
            </span>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Created:</span>
              <span className={styles.metaValue}>{createdOn}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Version:</span>
              <span className={styles.metaValue}>{data?.version_no ?? "-"}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItemTruncate} title={treatmentTitle}>
              <span className={styles.metaValue}>{treatmentTitle}</span>
            </div>
          </div>

          <div className={styles.topBarActions}>
            <button
              className={styles.iconBtn}
              onClick={downloadLatest}
              disabled={isDownloading || summaryQuery.isLoading}
              title="Download"
            >
              {/* simple download icon */}
              <DownloadIcon />
            </button>

            <button
              className={styles.outlineBtn}
              onClick={previewLatest}
              disabled={isDownloading || summaryQuery.isLoading}
            >
              <span className={styles.btnIcon}>👁</span>
              Preview
            </button>

            <button
              className={styles.outlineBtn}
              onClick={() => data && createNewVersion.mutate(data.id)}
              disabled={!data || createNewVersion.isLoading || !!data?.is_old_version}
              title={data?.is_old_version ? "Old versions can’t be edited" : "Edit as new version"}
            >
              <span className={styles.btnIcon}>✎</span>
              Update consent &amp; create new version
            </button>
          </div>
        </div>

        {/* Tabs row */}
        <div className={styles.tabs}>
          <button
            className={cx(styles.tab, { [styles.tabActive]: activeTab === "CONSENT" })}
            onClick={() => setActiveTab("CONSENT")}
          >
            Consent &amp; Patient
          </button>
          <button
            className={cx(styles.tab, { [styles.tabActive]: activeTab === "WAITING" })}
            onClick={() => setActiveTab("WAITING")}
          >
            Waiting Room
          </button>
          <button
            className={cx(styles.tab, { [styles.tabActive]: activeTab === "AUDIT" })}
            onClick={() => setActiveTab("AUDIT")}
          >
            Audit Log
          </button>
          <button
            className={cx(styles.tab, { [styles.tabActive]: activeTab === "DOCS" })}
            onClick={() => setActiveTab("DOCS")}
          >
            Documents &amp; Downloads
          </button>
        </div>

{showPrintConfirm && (
            <div className={styles.modalBackdrop} onClick={() => setShowPrintConfirm(false)}>
              <div
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className={styles.modalTitle}>Print Consent Form</h3>

                <p className={styles.modalText}>
                  I declare that I will present this to the patient to sign manually and I understand
                  there will be no digital audit log attached to this version.
                </p>

                <div className={styles.modalActions}>
                  <button
                    className={styles.outlineBtn}
                    onClick={() => setShowPrintConfirm(false)}
                  >
                    Cancel
                  </button>

                  <button
                  className={styles.primaryBtn}
                  disabled={manuallySign.isLoading || isDownloading}
                  onClick={async () => {
                    if (!data) return;

                    try {
                    
                      await manuallySign.mutateAsync(data.id);
                      setShowPrintConfirm(false);
                      await downloadLatest();
                    } catch (err) {
                      console.error("Manual sign + print failed", err);
                    }
                  }}
                >
                  I Understand, Print
                </button>
                </div>
              </div>
            </div>
          )}




        {/* Content */}
        {activeTab === "CONSENT" && (
          <>
            {/* Patient Details */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Patient details</h2>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Name</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={
                      patient
                        ? `${patient.title ?? ""} ${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()
                        : "-"
                    }
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Email address</label>
                  <input className={styles.input} type="text" readOnly value={patient?.email_address || "-"} />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Mobile number</label>
                  <input className={styles.input} type="text" readOnly value={patient?.mobile_phone || "-"} />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Phone</label>
                  <input className={styles.input} type="text" readOnly value={patient?.home_phone || "-"} />
                </div>

                <div className={cx(styles.field, styles.fullWidth)}>
                  <label className={styles.label}>Address</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={
                      patient
                        ? `${patient.address_line_1 || ""}${patient.address_line_2 ? `, ${patient.address_line_2}` : ""}`.trim() || "-"
                        : "-"
                    }
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>NHS Number</label>
                  <input className={styles.input} type="text" readOnly value={patient?.nhs_number || "-"} />
                </div>
              </div>
            </section>

            {/* Consent status */}
            <section className={cx(styles.card, styles.cardStatus)}>
              <div className={styles.statusHeader}>
                <h2 className={styles.cardTitle}>Consent status</h2>

                <div className={styles.statusRight}>
                  <span
                    className={cx(styles.statusCircle, {
                      [styles.circleGreen]: data?.status === "Completed",
                      [styles.circleOrange]: data?.status !== "Completed",
                    })}
                  />
                  <span
                    className={cx(styles.statusText, {
                      [styles.textGreen]: data?.status === "Completed",
                      [styles.textOrange]: data?.status !== "Completed",
                    })}
                  >
                    {data?.status || "—"}
                  </span>
                </div>
              </div>

              {data?.status === "Completed" ? (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>Patient signature status:</span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>SIGNED</span>
                  </div>

                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>Clinician signature status:</span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>SIGNED</span>
                  </div>
                </div>
              ) : (
                 <div className={styles.statusRows}>
                  {/* Manual sign + print */}
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Signed manually at the practice
                    </span>

                    <button
                      className={styles.outlineBtn}
                      onClick={() => setShowPrintConfirm(true)}
                      disabled={!data || manuallySign.isLoading}
                    >
                      🖨 Mark as signed manually & Print
                    </button>
                  </div>

                  {/* Clinician signature */}
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>Clinician signature</span>
                    <span className={cx(styles.smallPill, styles.pillOrange)}>
                      {data?.patient_signature?.signed_timestamp
                        ? "APPLIED"
                        : "NOT SIGNED"}
                    </span>
                  </div>
                </div>
              )}
            </section>

            <div className={styles.footer}>
              <button className={styles.outlineBtn} onClick={() => navigate("/new-dashboard")}>
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {copiedToast && (
        <div className={styles.copyToast}>
            <span className={styles.toastIcon}>✓</span>
            Link copied to clipboard
        </div>
        )}

        {activeTab === "WAITING" && (
          <>
            {/* Remote link */}

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Signed manually at the practice & Print consent form</h2>
            <p className={styles.paragraph}>
              Print a physical copy of the consent form for your patient to sign manually in person.
              <strong> The printed document will not include the audit log.</strong>
            </p>

            {data?.status === "Completed" ? (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>Patient signature status:</span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>SIGNED</span>
                  </div>

                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>Clinician signature status:</span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>SIGNED</span>
                  </div>
                </div>
              ) : (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                <button
                  className={styles.outlineBtn}
                  onClick={() => setShowPrintConfirm(true)}
                  disabled={!data || manuallySign.isLoading}
                >
                  🖨 Mark as signed manually and Print consent form
                </button>
                  </div>
                </div>
              )}
   
        </section>


          



            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Sign remotely</h2>
              <p className={styles.paragraph}>
                We’ve provided your patient with a secure link to view and complete their consent documents. If you’d
                like to resend the form manually via email or text, copy the link below.
              </p>

              <div className={styles.linkBox}>
                {data?.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`}
              </div>

              <button className={styles.outlineBtn} onClick={copyRemoteLink} disabled={!data}>
                Copy link
              </button>
            </section>

            {/* Waiting room QR */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Sign in waiting room</h2>
              <p className={styles.paragraph}>
                Generate a QR code to display in your waiting room. When scanned, your patient will be directed to
                their consent documents.
              </p>

              <button className={styles.outlineBtn} onClick={() => setShowQR(true)} disabled={!data}>
                Generate QR code
              </button>

              {showQR && (
                <div className={styles.qrBackdrop} onClick={() => setShowQR(false)}>
                  <div className={styles.qrModal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.qrWrap}>
                      <QRCode
                        value={data?.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`}
                      />
                    </div>
                    <button className={styles.outlineBtn} onClick={() => setShowQR(false)}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </section>

            <div className={styles.footer}>
              <button className={styles.outlineBtn} onClick={() => navigate("/new-dashboard")}>
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "DOCS" && (
  <>
    <section className={styles.card}>
      <div className={styles.docsHeader}>
        <h2 className={styles.cardTitle}>Document Versions</h2>
        <p className={styles.docsSub}>
          View and manage all versions of this consent document
        </p>
      </div>

      {/* Versions Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.versionsTable}>
          <thead>
            <tr>
              <th>Version</th>
              <th>Date</th>
              <th>Status</th>
              <th className={styles.alignRight}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {versionList.map((v) => {
              const status = versionStatusLabel(v, data?.version_no || 1);

              return (
                <tr key={v.consent_bundle_id}>
                  <td>
                    <div className={styles.versionCell}>
                      <strong>v{v.version}</strong>
                      <span className={styles.versionCode}>
                        {v.code}
                      </span>
                    </div>
                  </td>

                  <td>{formatDate(data?.created_at)}</td>

                  <td>
                    <span
                      className={cx(styles.statusTag, {
                        [styles.tagCurrent]: status === "Current",
                        [styles.tagSigned]: status === "Signed",
                        [styles.tagArchived]: status === "Archived",
                      })}
                    >
                      {status}
                    </span>
                  </td>

                  <td className={styles.alignRight}>
                    <div className={styles.actionIcons}>
                      {/* View */}
                      <button
                        className={styles.iconAction}
                        title="View"
                        onClick={() =>
                          navigate(`/summary/${v.consent_bundle_id}`)
                        }
                      >
                        👁
                      </button>

                      {/* Download */}
                      <button
                        className={styles.iconAction}
                        title="Download"
                        onClick={() =>
                          downloadVersion(v.consent_bundle_id)
                        }
                        disabled={isDownloading}
                      >
                        ⬇
                      </button>

                      {/* Open version */}
                      <button
                        className={styles.iconAction}
                        title="Open version"
                        onClick={() =>
                          navigate(`/summary/${v.consent_bundle_id}`)
                        }
                      >
                        ⟳
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Download full report */}
      {data?.version_no && data.version_no > 1 && (
        <button
          className={styles.outlineBtn}
          onClick={downloadFullReport}
          disabled={isDownloading}
        >
          Download Full Consent Report
        </button>
      )}

      <hr className={styles.divider} />

      {/* Flag patient complaint */}
      <button
        className={styles.dangerBtn}
        disabled
        title="This feature will be available soon"
      >
        ⚠ Flag Patient Complaint
      </button>

      <p className={styles.mutedSmall}>
        Use this if a patient has raised a complaint. This will flag the
        case and prompt you to download the full consent report for your
        records.
      </p>
    </section>

    <div className={styles.footer}>
      <button
        className={styles.outlineBtn}
        onClick={() => navigate("/new-dashboard")}
      >
        Go to dashboard
      </button>
    </div>
  </>
)}


       {activeTab === "AUDIT" && (
  <>
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Audit log</h2>

      {/* Loading */}
      {quickViewLoading && (
        <p className={styles.muted}>Loading audit log…</p>
      )}

      {/* Error */}
      {quickViewError && (
        <p className={styles.errorText}>{quickViewError}</p>
      )}

      {/* Timeline */}
      {!quickViewLoading &&
        !quickViewError &&
        Object.keys(interactionLog || {}).length > 0 && (
          <div className={styles.auditTimeline}>
            {Object.entries(interactionLog).map(
              ([date, events]: any) => (
                <div key={date} className={styles.auditDay}>
                  <div className={styles.auditDate}>{date}</div>

                  <div className={styles.auditEvents}>
                    {events.map((item: any, idx: number) => (
                      <div key={idx} className={styles.auditEvent}>
                        <div className={styles.auditDot} />

                        <div className={styles.auditContent}>
                          <p className={styles.auditTitle}>
                            {item.event}
                          </p>

                          {item.description?.length > 0 && (
                            <ul className={styles.auditDesc}>
                              {item.description.map(
                                (d: string, i: number) => (
                                  <li key={i}>{d}</li>
                                )
                              )}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}

      {!quickViewLoading &&
        !quickViewError &&
        Object.keys(interactionLog || {}).length === 0 && (
          <p className={styles.muted}>
            No audit log entries available.
          </p>
        )}
    </section>

    <div className={styles.footer}>
      <button
        className={styles.outlineBtn}
        onClick={() => navigate("/new-dashboard")}
      >
        Go to dashboard
      </button>
    </div>
  </>
)}

      </div>
    </>
  );
}

/* -------------------------- Local helper (name) -------------------------- */
function getPdfName(s: SummaryDTO | any) {
  const patient = s?.patient;
  const name = patient ? `${patient.first_name || ""}_${patient.last_name || ""}`.replace(/\s+/g, "_") : "patient";
  const ver = s?.version_no ? `v${s.version_no}` : "v1";
  return `consent_${name}_${ver}.pdf`;
}
